import {useCallback} from "react";
import {CompanyDisplayDTO} from "@/api/data-contracts";
import {useAuth} from "@/app/auth/AuthContext";
import {matchPath, useLocation} from "react-router-dom";
import {AppPaths} from "@/app/AppPaths";

export type CompanySwitcherHookResult = {
    availableCompanies: CompanyDisplayDTO[];
    activeCompany: CompanyDisplayDTO | undefined;
    switchCompany: (companyId: number) => Promise<void>;
};

/**
 * Provides company-switching state and actions for the header switcher.
 *
 * Derives available companies and the active company from the current session
 * via AuthContext. Delegates the switch action to AuthContext.switchCompany,
 * which calls POST /api/auth/switch-company/{companyId} and updates the session.
 *
 * After a successful switch:
 * - If the user is on the company detail page, performs a full page navigation
 *   to the new company's detail URL so the component remounts cleanly with
 *   the correct company data.
 * - If the user is on the user detail page, performs a full page navigation
 *   to the same user URL with the updated companyId query parameter so the
 *   correct company-scoped settings are loaded.
 * - If the user is on an internal request detail page (not create/clone),
 *   navigates to the internal request overview so stale request data is not shown.
 * - If the user is on an external request detail page,
 *   navigates to the external request overview so stale request data is not shown.
 * - Otherwise, performs a full page reload so all components re-initialize
 *   with the new company context and fresh data.
 *
 * @returns availableCompanies - All other companies the user may switch to.
 * @returns activeCompany - The currently active company from the session.
 * @returns switchCompany - Triggers a company switch for the given company ID.
 */
export function useCompanySwitcher(): CompanySwitcherHookResult {
    const { company, otherCompanyMemberships, switchCompany: switchCompanyInContext } = useAuth();
    const location = useLocation();

    const switchCompany = useCallback(
        async (companyId: number) => {
            await switchCompanyInContext(companyId);

            const companyMatch = matchPath(AppPaths.COMPANY, location.pathname);
            if (companyMatch) {
                globalThis.location.href =
                    `${globalThis.location.origin + globalThis.location.pathname  }#${  AppPaths.COMPANY_fn(companyId)}`;
                return;
            }

            const userMatch = matchPath(AppPaths.USER, location.pathname);
            if (userMatch?.params?.id) {
                const userId = Number(userMatch.params.id);
                globalThis.location.href =
                    `${globalThis.location.origin +
                    globalThis.location.pathname 
                    }#${ 
                    AppPaths.USER_fn(userId, companyId)}`;
                return;
            }

            const requestMatch = matchPath(AppPaths.INTERNAL_REQUEST, location.pathname);
            const requestId = requestMatch?.params?.id;
            if (requestMatch && requestId && requestId !== "create" && requestId !== "clone") {
                globalThis.location.href =
                    `${globalThis.location.origin +
                    globalThis.location.pathname 
                    }#${ 
                    AppPaths.INTERNAL_REQUEST_OVERVIEW}`;
                return;
            }

            const externalRequestMatch = matchPath(AppPaths.EXTERNAL_REQUEST, location.pathname);
            const externalRequestId = externalRequestMatch?.params?.id;
            if (externalRequestMatch && externalRequestId) {
                globalThis.location.href =
                    `${globalThis.location.origin +
                    globalThis.location.pathname 
                    }#${ 
                    AppPaths.EXTERNAL_REQUEST_OVERVIEW}`;
                return;
            }

            globalThis.location.reload();
        },
        [switchCompanyInContext, location.pathname]
    );

    return {
        availableCompanies: otherCompanyMemberships ?? [],
        activeCompany: company,
        switchCompany
    };
}
